import { DefaultOptionType } from "antd/es/select";

import dayjs from "dayjs";
import { defaults } from "lodash-es";

import { MARGIN_TYPE_DISPLAY } from "@utils/displays";
import {
  ACCOUNT_TYPE,
  BENEFIT_CARD_WITHDRAWAL_TYPE,
  MARGIN_TYPE,
  PRODUCT_TYPE,
} from "@utils/enums";

export const shouldHaveUnifiedMarginOption = (
  marginOptions: { value: MARGIN_TYPE; label: string }[],
  isManualTyping?: boolean,
) => {
  if (!isManualTyping && marginOptions.length >= 2) {
    const hasPurchaseMargin = marginOptions.some(
      ({ value }) => value === MARGIN_TYPE.MARGEM_COMPRA,
    );
    const hasWithdrawalMargin = marginOptions.some(
      ({ value }) => value === MARGIN_TYPE.MARGEM_SAQUE,
    );

    return hasPurchaseMargin && hasWithdrawalMargin;
  }

  return false;
};

export const getMarginOptions = (
  products: NonNullable<
    JourneyDataBenefitCard["covenantData"]
  >["produtos"] = [],
  productType?: number | null,
  hideUnified?: boolean,
) => {
  type ProductWithMarginType = Omit<
    NonNullable<typeof products>[0],
    "tipo_margem"
  > & { tipo_margem: MARGIN_TYPE };

  const options = products
    .filter(
      (p): p is ProductWithMarginType =>
        p.codigo_produto === productType && !!p.tipo_margem,
    )
    .map((p) => ({
      value: p.tipo_margem,
      label: MARGIN_TYPE_DISPLAY[p.tipo_margem],
    }));

  if (!hideUnified && shouldHaveUnifiedMarginOption(options)) {
    options.push({
      value: MARGIN_TYPE.MARGEM_UNIFICADA,
      label: MARGIN_TYPE_DISPLAY[MARGIN_TYPE.MARGEM_UNIFICADA],
    });
  }

  // Assure unique values
  return [...new Map(options.map((item) => [item.value, item])).values()];
};

export const isUnifiableMarginType = (marginType: MARGIN_TYPE) => {
  return [MARGIN_TYPE.MARGEM_COMPRA, MARGIN_TYPE.MARGEM_SAQUE].includes(
    marginType,
  );
};

export const getOptionsFromList = <T extends Record<string, unknown>>(
  list: T[] | undefined,
  labelFields: Array<keyof T>,
  valueFields: Array<keyof T>,
): DefaultOptionType[] => {
  if (!list) return [];

  return list.map((item) => ({
    label: labelFields.map((field) => item[field]).join(" - "),
    value: valueFields.map((field) => item[field]).join(" - "),
  }));
};

export const cardDataToMarginRawData = (
  cardData: CardRawData,
): MarginRawData => {
  const defaultValues: MarginRawData = {
    margem_atual: undefined,
    margem_compra: undefined,
    margem_saque: undefined,
    folha: undefined,
    folha_compra: undefined,
    folha_saque: undefined,
    verba: undefined,
    verba_compra: undefined,
    verba_saque: undefined,
    tipo_produto: null,
    tipo_margem: MARGIN_TYPE.MARGEM_UNICA,
    ocupacao: undefined,
    numero_matricula: undefined,
    senha_servidor: undefined,
    convenio_siape: undefined,
    classificacao_siape: undefined,
    tipo_vinculo: undefined,
    instituidor: undefined,
    tipo_credito: undefined,
  };

  return defaults(cardData, defaultValues);
};

export const debtDataToMarginRawData = (
  debtData: Partial<DebtData>,
): RepurchaseMarginRawData => {
  const defaultValues: RepurchaseMarginRawData = {
    margem_atual: undefined,
    tipo_produto: null,
    tipo_produto_cartao: null,
    tipo_margem: null,
    numero_matricula: undefined,
    senha_servidor: undefined,
  };

  return defaults(debtData, defaultValues);
};

export const getHighestInstallmentGroup = (
  withdrawalLimitGroup: WithdrawalLimitGroup[],
) => {
  return withdrawalLimitGroup.reduce<WithdrawalLimitGroup | null>(
    (highestGroup, currentGroup) => {
      if (!highestGroup) return currentGroup;
      return currentGroup.installments > highestGroup.installments
        ? currentGroup
        : highestGroup;
    },
    null,
  );
};

export const getInstallmentGroup = (
  withdrawalLimitGroup: WithdrawalLimitGroup[],
  maxInstallments?: number | null,
) => {
  return getHighestInstallmentGroup(
    withdrawalLimitGroup.filter(
      (group) => group.installments < (maxInstallments || Infinity),
    ),
  );
};

export const getWithdrawalTypeFromLimitSimulation = (
  limitSimulation: LimitSimulation,
  marginType?: MARGIN_TYPE | null,
) => {
  if (
    marginType &&
    [
      MARGIN_TYPE.MARGEM_SAQUE,
      MARGIN_TYPE.MARGEM_UNICA,
      MARGIN_TYPE.MARGEM_UNIFICADA,
    ].includes(marginType)
  ) {
    if (limitSimulation.allowInstallmentWithdrawal) {
      return BENEFIT_CARD_WITHDRAWAL_TYPE.PARCELADO;
    }

    if (limitSimulation.allowCashWithdrawal) {
      return BENEFIT_CARD_WITHDRAWAL_TYPE.A_VISTA;
    }
  }

  return BENEFIT_CARD_WITHDRAWAL_TYPE.SEM_SAQUE;
};

export const getDataFromLimitSimulation = (
  limitSimulation: LimitSimulation,
  withdrawalType: BENEFIT_CARD_WITHDRAWAL_TYPE,
): Partial<CardData> => {
  let preApprovedLimit = limitSimulation.preApprovedLimit;
  let maxWithdrawalValue = limitSimulation.maxWithdrawalValue;
  let installments = 0;
  const idSpecialTax: number | undefined =
    withdrawalType !== BENEFIT_CARD_WITHDRAWAL_TYPE.SEM_SAQUE
      ? limitSimulation?.specialTax?.usedTaxSpecial
      : undefined;

  if (withdrawalType === BENEFIT_CARD_WITHDRAWAL_TYPE.PARCELADO) {
    if (limitSimulation.hasWithdrawalLimitGroups) {
      const highestInstallmentGroup = getHighestInstallmentGroup(
        limitSimulation.withdrawalLimitGroups,
      );

      if (highestInstallmentGroup) {
        preApprovedLimit = highestInstallmentGroup.preApprovedLimit;
        maxWithdrawalValue = highestInstallmentGroup.maxWithdrawal;
        installments = highestInstallmentGroup.installments;
      }
    } else {
      installments = Math.max(...limitSimulation.deprecatedInstallmentGroups);
    }
  }

  return {
    withdrawalType,
    preApprovedLimit,
    maxWithdrawalValue,
    installments,
    idSpecialTax,
  };
};

export const getInitialDataFromLimitSimulation = (
  limitSimulation: LimitSimulation,
  marginType?: MARGIN_TYPE | null,
): Partial<CardData> => {
  const withdrawalType = getWithdrawalTypeFromLimitSimulation(
    limitSimulation,
    marginType,
  );

  return getDataFromLimitSimulation(limitSimulation, withdrawalType);
};

export const findInstallmentGroup = (
  installments?: number | null,
  withdrawalLimitGroups: WithdrawalLimitGroup[] = [],
) => {
  if (!installments) return getHighestInstallmentGroup(withdrawalLimitGroups);

  return withdrawalLimitGroups.find(
    (group) => group.installments === installments,
  );
};

export const getCardDataFromSimulations = (params: {
  withdrawalType?: BENEFIT_CARD_WITHDRAWAL_TYPE;
  limitSimulation: Partial<LimitSimulation>;
  withdrawalSimulation?: WithdrawalSimulation | null;
  installments?: number | null;
  withdrawalValue?: number | null;
  specialTaxId?: number;
}) => {
  const {
    withdrawalType,
    limitSimulation,
    withdrawalSimulation,
    installments,
    withdrawalValue,
    specialTaxId,
  } = params;

  const data: Partial<CardData> = {
    withdrawalType: withdrawalType,
    preApprovedLimit: limitSimulation.preApprovedLimit,
    maxWithdrawalValue: limitSimulation.maxWithdrawalValue,
    productTax: limitSimulation.productTax,
    annualProductTax: limitSimulation.annualProductTax,
    monthlyCet: limitSimulation.monthlyCet,
    annualCet: limitSimulation.annualCet,
    withdrawalValue: null,
    iofTotalValue: null,
    totalFinancedAmount: null,
    totalValueToPay: null,
    dueDate: null,
    installments: null,
    installmentValue: null,

    hasWithdrawal: withdrawalType === BENEFIT_CARD_WITHDRAWAL_TYPE.A_VISTA,
    hasInstallmentWithdrawal:
      withdrawalType === BENEFIT_CARD_WITHDRAWAL_TYPE.PARCELADO,
    preApprovedPurchaseLimit: limitSimulation.preApprovedPurchaseLimit,
    preApprovedWithdrawalLimit: limitSimulation.preApprovedWithdrawalLimit,
    iofAdditionalValue: null,
    iofDailyValue: null,
    dueDateDay: limitSimulation.dueDateDay,
    specialTax: !!specialTaxId && specialTaxId >= 0 ? true : false,
    idSpecialTax: specialTaxId,
  };

  if (limitSimulation.issuanceFee) {
    data.issuanceFee = limitSimulation.issuanceFee;
  }

  if (withdrawalSimulation) {
    if (withdrawalType === BENEFIT_CARD_WITHDRAWAL_TYPE.A_VISTA) {
      const { cashWithdrawalSimulation } = withdrawalSimulation;

      data.withdrawalValue = withdrawalValue;
      data.productTax = cashWithdrawalSimulation.productTax;
      data.annualProductTax = cashWithdrawalSimulation.annualProductTax;
      data.monthlyCet = cashWithdrawalSimulation.monthlyCet;
      data.annualCet = cashWithdrawalSimulation.annualCet;
      data.iofTotalValue = cashWithdrawalSimulation.iofTotalValue;
      data.totalFinancedAmount = cashWithdrawalSimulation.totalFinancedAmount;
      data.dueDate = cashWithdrawalSimulation.dueDate;
      if (data.dueDateDay) {
        data.dueDateDay = dayjs(cashWithdrawalSimulation.dueDate).format("DD");
      }
      data.iofAdditionalValue =
        cashWithdrawalSimulation.iofAdditionalValueInCash;
      data.iofDailyValue = cashWithdrawalSimulation.iofDailyTax;
    } else if (withdrawalType === BENEFIT_CARD_WITHDRAWAL_TYPE.PARCELADO) {
      const { installmentWithdrawalSimulation } = withdrawalSimulation;
      const {
        withdrawalLimitGroups,
        withdrawalSpecialTaxesLimitGroups,
        specialTax,
      } = limitSimulation;

      let group = findInstallmentGroup(installments, withdrawalLimitGroups);

      if (
        !!specialTax &&
        Object.keys(withdrawalSpecialTaxesLimitGroups ?? {}).length > 0
      ) {
        group = findInstallmentGroup(
          installments,
          withdrawalSpecialTaxesLimitGroups?.[
            (specialTaxId || "convenio").toString()
          ],
        );
      }

      if (group) {
        data.preApprovedLimit = group.preApprovedLimit;
        data.maxWithdrawalValue = group.maxWithdrawal;
      }

      data.withdrawalValue = withdrawalValue;
      data.productTax = installmentWithdrawalSimulation.productTax;
      data.annualProductTax = installmentWithdrawalSimulation.annualProductTax;
      data.monthlyCet = installmentWithdrawalSimulation.monthlyCet;
      data.annualCet = installmentWithdrawalSimulation.annualCet;
      data.iofTotalValue = installmentWithdrawalSimulation.totalIofValueInCash;
      data.totalFinancedAmount =
        installmentWithdrawalSimulation.totalFinancedAmount;
      data.dueDate = installmentWithdrawalSimulation.firstInstallmentDue;
      data.iofAdditionalValue =
        installmentWithdrawalSimulation.iofAdditionalTax;
      data.iofDailyValue = installmentWithdrawalSimulation.iofDailyTax;
      data.totalValueToPay = installmentWithdrawalSimulation.totalValueToPay;
      data.installments = group?.installments || installments || null;
      data.installmentValue = installmentWithdrawalSimulation.installmentValue;
    }
  }

  return data;
};

export const cardDataToRawCardData = (
  cardData: Partial<CardData>,
): CardRawData => {
  return {
    tipo_saque: cardData.withdrawalType,
    limite_pre_aprovado: cardData.preApprovedLimit,
    valor_saque: cardData.withdrawalValue,
    valor_disponivel_saque: cardData.maxWithdrawalValue,
    taxa: cardData.productTax,
    taxa_efetiva_ano: cardData.annualProductTax,
    cet_mes: cardData.monthlyCet,
    cet_ano: cardData.annualCet,
    vr_iof_total: cardData.iofTotalValue,
    valor_financiado: cardData.totalFinancedAmount,
    dt_vencimento_fatura: cardData.dueDate,
    qtd_parcela_saque_parcelado: cardData.installments,
    valor_parcela: cardData.installmentValue,
    valor_total_a_pagar: cardData.totalValueToPay,

    possui_saque: cardData.hasWithdrawal,
    saque_parcelado: cardData.hasInstallmentWithdrawal,
    limite_pre_aprovado_compra: cardData.preApprovedPurchaseLimit,
    limite_pre_aprovado_saque: cardData.preApprovedWithdrawalLimit,
    vr_iof_adicional: cardData.iofAdditionalValue,
    vr_iof: cardData.iofDailyValue,
    vencimento_fatura: cardData.dueDateDay ?? cardData.dueDate,
    seguro: !!cardData.insurance,
    plano:
      cardData.insurancePlans?.map((plan) => ({
        id: plan.id,
        valor: plan.value,
        obrigatorio: plan.required,
      })) || [],
    taxa_especial: Boolean(cardData.specialTax),
    id_taxa_especial: cardData.specialTax ? cardData.idSpecialTax : undefined,
    tarifa_emissao: cardData.issuanceFee
      ? {
          qtd_parcelas: cardData.issuanceFee.installments,
          valor_parcela: cardData.issuanceFee.installmentValue,
          valor_total: cardData.issuanceFee.totalValue,
          parcelado: !!cardData.issuanceFee.isInstallment,
        }
      : undefined,
    customerErrors: cardData.customerErrors ?? undefined,
  };
};

export const debtDataToRawCardData = (
  cardData: Partial<DebtData>,
): DebtRawData => {
  return {
    valor_quitacao: cardData.releaseValue,
    cet_ano_quitacao: cardData.releaseAnnualCet,
    cet_mes_quitacao: cardData.releaseMonthlyCet,
    qtd_parcelas_quitacao: cardData.releaseInstallments,
    taxa_quitacao: cardData.releaseProductTax,
    taxa_efetiva_ano_quitacao: cardData.releaseAnnualProductTax,
    valor_financiado_quitacao: cardData.releaseTotalFinancedAmount,
    valor_parcela_quitacao: cardData.releaseInstallmentValue,
    valor_total_a_pagar_quitacao: cardData.releaseTotalValueToPay,
    vencimento_fatura_quitacao:
      cardData.releaseDueDateDay ?? cardData.releaseDueDate,
    dt_vencimento_fatura_quitacao: cardData.releaseDueDate,
    vr_iof_quitacao: cardData.releaseIofDailyValue,
    vr_iof_adicional_quitacao: cardData.releaseIofAdditionalValue,
    vr_iof_total_quitacao: cardData.releaseIofTotalValue,

    tipo_saque: cardData.withdrawalType,
    limite_pre_aprovado: cardData.preApprovedLimit,
    valor_saque: cardData.withdrawalValue,
    valor_disponivel_saque: cardData.maxWithdrawalValue,
    taxa: cardData.productTax,
    taxa_efetiva_ano: cardData.annualProductTax,
    cet_mes: cardData.monthlyCet,
    cet_ano: cardData.annualCet,
    vr_iof_total: cardData.iofTotalValue,
    valor_financiado: cardData.totalFinancedAmount,
    dt_vencimento_fatura: cardData.dueDate,
    qtd_parcela_saque_parcelado: cardData.installments,
    valor_parcela: cardData.installmentValue,
    valor_total_a_pagar: cardData.totalValueToPay,

    possui_saque: cardData.hasWithdrawal,
    saque_parcelado: cardData.hasInstallmentWithdrawal,
    limite_pre_aprovado_compra: cardData.preApprovedPurchaseLimit,
    limite_pre_aprovado_saque: cardData.preApprovedWithdrawalLimit,
    vr_iof_adicional: cardData.iofAdditionalValue,
    vr_iof: cardData.iofDailyValue,
    vencimento_fatura: cardData.dueDateDay ?? cardData.dueDate,
    seguro: !!cardData.insurance,
    plano:
      cardData.insurancePlans?.map((plan) => ({
        id: plan.id,
        valor: plan.value,
        obrigatorio: plan.required,
      })) || [],
    taxa_especial: Boolean(cardData.specialTax),
    id_taxa_especial: cardData.specialTax
      ? (cardData.idSpecialTax ?? undefined)
      : undefined,
    tarifa_emissao: cardData.issuanceFee
      ? {
          qtd_parcelas: cardData.issuanceFee.installments,
          valor_parcela: cardData.issuanceFee.installmentValue,
          valor_total: cardData.issuanceFee.totalValue,
          parcelado: !!cardData.issuanceFee.isInstallment,
        }
      : undefined,
    customerErrors: cardData.customerErrors ?? undefined,
    allowReleaseSimulation: cardData.allowReleaseSimulation,
    allowWithdrawalSimulation: cardData.allowWithdrawalSimulation,
  };
};

export const rawCardDatatoCardData = (rawCardData: CardRawData): CardData => {
  return {
    withdrawalType:
      rawCardData.tipo_saque || BENEFIT_CARD_WITHDRAWAL_TYPE.SEM_SAQUE,
    preApprovedLimit: rawCardData.limite_pre_aprovado || 0,
    withdrawalValue: rawCardData.valor_saque || 0,
    maxWithdrawalValue: rawCardData.valor_disponivel_saque || 0,
    productTax: rawCardData.taxa || 0,
    annualProductTax: rawCardData.taxa_efetiva_ano || 0,
    monthlyCet: rawCardData.cet_mes || 0,
    annualCet: rawCardData.cet_ano || 0,
    iofTotalValue: rawCardData.vr_iof_total || null,
    totalFinancedAmount: rawCardData.valor_financiado || null,
    dueDate: rawCardData.dt_vencimento_fatura || null,
    installments: rawCardData.qtd_parcela_saque_parcelado || null,
    installmentValue: rawCardData.valor_parcela || null,
    totalValueToPay: rawCardData.valor_total_a_pagar || null,

    hasWithdrawal: !!rawCardData.possui_saque,
    hasInstallmentWithdrawal: !!rawCardData.saque_parcelado,
    preApprovedPurchaseLimit: rawCardData.limite_pre_aprovado_compra || 0,
    preApprovedWithdrawalLimit: rawCardData.limite_pre_aprovado_saque || 0,
    iofAdditionalValue: rawCardData.vr_iof_adicional || null,
    iofDailyValue: rawCardData.vr_iof || null,
    dueDateDay: rawCardData.vencimento_fatura || "",
    insurance: !!rawCardData.seguro,
    insurancePlans:
      rawCardData.plano?.map((plan) => ({
        id: plan.id,
        value: plan.valor,
        required: plan.obrigatorio,
      })) || [],
  };
};

export const rawDebtDatatoCardData = (rawCardData: DebtRawData): DebtData => {
  return {
    releaseValue: rawCardData.valor_quitacao || 0,
    releaseProductTax: rawCardData.taxa_quitacao || 0,
    releaseAnnualProductTax: rawCardData.taxa_efetiva_ano_quitacao || 0,
    releaseMonthlyCet: rawCardData.cet_mes_quitacao || 0,
    releaseAnnualCet: rawCardData.cet_ano_quitacao || 0,
    releaseTotalFinancedAmount: rawCardData.valor_total_a_pagar_quitacao || 0,
    releaseTotalValueToPay: rawCardData.valor_total_a_pagar_quitacao || 0,
    releaseDueDate: rawCardData.dt_vencimento_fatura_quitacao || "",
    releaseInstallments: rawCardData.qtd_parcelas_quitacao || 0,
    releaseInstallmentValue: rawCardData.valor_parcela_quitacao || 0,
    releaseDueDateDay: rawCardData.vencimento_fatura_quitacao || "",
    hasRelease: true,
    releaseIofTotalValue: rawCardData.vr_iof_total_quitacao || 0,
    releaseIofAdditionalValue: rawCardData.vr_iof_adicional_quitacao || 0,
    releaseIofDailyValue: rawCardData.vr_iof_quitacao || 0,
    allowReleaseSimulation: rawCardData.allowReleaseSimulation,

    withdrawalType:
      rawCardData.tipo_saque || BENEFIT_CARD_WITHDRAWAL_TYPE.SEM_SAQUE,
    preApprovedLimit: rawCardData.limite_pre_aprovado || 0,
    withdrawalValue: rawCardData.valor_saque || 0,
    maxWithdrawalValue: rawCardData.valor_disponivel_saque || 0,
    productTax: rawCardData.taxa || 0,
    annualProductTax: rawCardData.taxa_efetiva_ano || 0,
    monthlyCet: rawCardData.cet_mes || 0,
    annualCet: rawCardData.cet_ano || 0,
    totalFinancedAmount: rawCardData.valor_financiado || null,
    dueDate: rawCardData.dt_vencimento_fatura || null,
    installments: rawCardData.qtd_parcela_saque_parcelado || null,
    installmentValue: rawCardData.valor_parcela || null,
    totalValueToPay: rawCardData.valor_total_a_pagar || null,

    iofTotalValue: rawCardData.vr_iof_total || null,
    iofAdditionalValue: rawCardData.vr_iof_adicional || null,
    iofDailyValue: rawCardData.vr_iof || null,

    hasWithdrawal: !!rawCardData.possui_saque,
    hasInstallmentWithdrawal: !!rawCardData.saque_parcelado,
    preApprovedPurchaseLimit: rawCardData.limite_pre_aprovado_compra || 0,
    preApprovedWithdrawalLimit: rawCardData.limite_pre_aprovado_saque || 0,
    allowWithdrawalSimulation: rawCardData.allowWithdrawalSimulation,
    dueDateDay: rawCardData.vencimento_fatura || "",
    insurance: !!rawCardData.seguro,
    insurancePlans:
      rawCardData.plano?.map((plan) => ({
        id: plan.id,
        value: plan.valor,
        required: plan.obrigatorio,
      })) || [],
  };
};

export const customerBankDataToBankData = (
  customerBankData: NonNullable<Customer["bankData"]>[0],
): BankData => {
  return {
    bank: customerBankData.conta_banco || "",
    type:
      customerBankData.conta_tipo || ACCOUNT_TYPE.CONTA_CORRENTE_PESSOA_FISICA,
    agency: customerBankData.conta_agencia || "",
    number: customerBankData.conta_numero,
    digit: customerBankData.conta_digito,
  };
};

export const bankDataToCustomerBankData = (
  bankData: BankData,
): NonNullable<Customer["bankData"]>[0] => {
  return {
    conta_banco: bankData.bank,
    conta_tipo: bankData.type,
    conta_agencia: bankData.agency,
    conta_numero: bankData.number,
    conta_digito: bankData.digit,
  };
};

export const getFolhaFieldName = (marginType?: MARGIN_TYPE | null) => {
  switch (marginType) {
    case MARGIN_TYPE.MARGEM_COMPRA:
      return "folha_compra";
    case MARGIN_TYPE.MARGEM_SAQUE:
      return "folha_saque";
    default:
      return "folha";
  }
};

export const getVerbaFieldName = (marginType?: MARGIN_TYPE | null) => {
  switch (marginType) {
    case MARGIN_TYPE.MARGEM_COMPRA:
      return "verba_compra";
    case MARGIN_TYPE.MARGEM_SAQUE:
      return "verba_saque";
    default:
      return "verba";
  }
};

export const isCardProductType = (productType?: PRODUCT_TYPE) => {
  if (!productType) return false;

  return [
    PRODUCT_TYPE.COMPRA_CLIENTE_BENEFICIO,
    PRODUCT_TYPE.COMPRA_CLIENTE_CONSIGNADO,
    PRODUCT_TYPE.CARTAO_BENEFICIO,
    PRODUCT_TYPE.CARTAO_BENEFICIO_REPRESENTANTE,
    PRODUCT_TYPE.CARTAO_CONSIGNADO,
    PRODUCT_TYPE.SAQUE_COMPLEMENTAR,
  ].includes(productType);
};

export const checkVulnerability = (
  status?: string | null,
  vulnerabilitiesAccepted = ["alta", "moderada"],
): boolean => {
  return vulnerabilitiesAccepted.includes(status?.toLocaleLowerCase() ?? "");
};
